// src/pages/personal/analytics/FeedbackSection/components/CommentsTable/CommentsTable.tsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataList, DataListColumnDef } from '@/components/ui/DataList';
import { COMPONENT_STYLES } from '@/theme';
import { TEXT_STYLES } from '@/theme/typography';
import { Grid, Flex, Card } from '@/components/ui/Layout';
import { useResponsive } from '@/hooks/useResponsive';
import { formatSmartDate } from '@/utils/dateUtils';
import styles from '../../FeedbackSection.module.css';
import { Table as UITable, TableColumn } from '@/components/ui/Table/Table';

interface WordData {
  text: string;
  value: number;
}

interface CommentData {
  id: string;
  text: string;
  rating: number;
  date: string;
  platform: string;
  formattedDate?: string;
}

interface CommentsTableProps {
  comments: CommentData[];
  wordCloudData: WordData[];
  period?: string; // Используется для информационных целей
  businessTimezone?: string; // Используется для форматирования дат
}

// Интерфейс для фильтров
interface CommentFilters {
  keyword: string;
  rating: number | null;
  dateFrom: string | null;
  dateTo: string | null;
}

export const CommentsTable: React.FC<CommentsTableProps> = ({ 
  comments, 
  wordCloudData,
  // Убираем неиспользуемые параметры из деструктуризации
  // но оставляем их в интерфейсе для совместимости
}) => {
  const { t, i18n } = useTranslation('analytics');
  const { isMobile } = useResponsive();
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;
  
  // Состояние для фильтров
  const [filters, setFilters] = useState<CommentFilters>({
    keyword: '',
    rating: null,
    dateFrom: null,
    dateTo: null
  });

  // Получаем предложения для поиска ключевых слов на основе облака слов
  const keywordSuggestions = wordCloudData.map(item => item.text);

  // Функция для применения фильтров к комментариям
  const filteredComments = useMemo(() => {
    return comments.filter(comment => {
      // Фильтр по ключевому слову
      if (filters.keyword && !comment.text.toLowerCase().includes(filters.keyword.toLowerCase())) {
        return false;
      }

      // Фильтр по рейтингу
      if (filters.rating !== null && comment.rating !== filters.rating) {
        return false;
      }

      // Фильтр по дате (от)
      if (filters.dateFrom) {
        const commentDate = new Date(comment.date);
        const fromDate = new Date(filters.dateFrom);
        
        // Используем прямое сравнение дат
        if (commentDate < fromDate) {
          return false;
        }
      }

      // Фильтр по дате (до)
      if (filters.dateTo) {
        const commentDate = new Date(comment.date);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Конец дня
        
        // Используем прямое сравнение дат 
        if (commentDate > toDate) {
          return false;
        }
      }

      return true;
    });
  }, [comments, filters]);

  // Пагинация
  const paginatedComments = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredComments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredComments, page, itemsPerPage]);

  // Функция для обновления фильтров
  const handleFilterChange = (key: keyof CommentFilters, value: string | number | null) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Сбрасываем страницу при изменении фильтров
  };

  // Функция для сброса всех фильтров
  const resetFilters = () => {
    setFilters({
      keyword: '',
      rating: null,
      dateFrom: null,
      dateTo: null
    });
    setPage(1);
  };

  // Получение эмодзи для рейтинга - обновлено для 3-балльной шкалы
  const getRatingEmoji = (rating: number): string => {
    switch (rating) {
      case 1: return '👎'; // Плохо
      case 2: return '✋'; // Есть над чем работать
      case 3: return '👍'; // Хорошо
      default: return '';
    }
  };

  // Определение колонок для DataList
  const columns: DataListColumnDef<CommentData>[] = [
    {
      key: 'date',
      title: t('feedback.comments.date'),
      width: isMobile ? 'sm' : 'md',
      textAlign: 'left',
      render: (item) => {
        // Используем предварительно отформатированную дату если она есть, 
        // иначе форматируем на месте
        return item.formattedDate || formatSmartDate(item.date, true, i18n.language);
      }
    },
    {
      key: 'rating',
      title: t('feedback.comments.rating'),
      width: 'sm',
      textAlign: 'center',
      render: (item) => (
        <span title={t(`feedback.ratings.r${item.rating}`)} className="text-lg">
          {getRatingEmoji(item.rating)}
        </span>
      )
    },
    {
      key: 'text',
      title: t('feedback.comments.comment'),
      width: 'auto',
      textAlign: 'left'
    }
  ];

  // Определение колонок для UITable (используется в мобильном режиме)
  const tableColumns: TableColumn<CommentData>[] = useMemo(() => [
    {
      key: 'date',
      title: t('feedback.comments.date'),
      width: 'md',
      render: (comment) => (
        <span>
          {comment.formattedDate || formatSmartDate(comment.date, true, i18n.language)}
        </span>
      ),
      mobilePriority: 1
    },
    {
      key: 'rating',
      title: t('feedback.comments.rating'),
      width: 'sm',
      textAlign: 'center',
      render: (comment) => (
        <span title={t(`feedback.ratings.r${comment.rating}`)} className="text-lg">
          {getRatingEmoji(comment.rating)}
        </span>
      ),
      mobilePriority: 0
    },
    {
      key: 'text',
      title: t('feedback.comments.comment'),
      width: 'auto',
      truncate: true,
      maxLength: 100,
      render: (comment) => (
        <span>{comment.text}</span>
      ),
      mobilePriority: 2
    }
  ], [t, i18n.language]);

  // Расчет общего количества страниц
  const totalPages = Math.ceil(filteredComments.length / itemsPerPage);

  return (
    <div>
      <div className={styles.tableFiltersContainer}>
        <h4 className="font-medium mb-3">{t('feedback.comments.filters')}</h4>
        <Grid 
          cols={isMobile ? 1 : 2}
          gap="md"
          className={styles.filtersGrid}
        >
          {/* Первый ряд фильтров */}
          <Flex direction="column" gap="sm">
            {/* Фильтр по рейтингу - обновлено для 3-балльной шкалы */}
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('feedback.comments.rating')}
              </label>
              <select
                value={filters.rating || ''}
                onChange={(e) => handleFilterChange('rating', e.target.value ? parseInt(e.target.value) : null)}
                className={COMPONENT_STYLES.form.input}
              >
                <option value="">{t('feedback.comments.all_ratings')}</option>
                <option value="1">1 - {t('feedback.ratings.r1')}</option>
                <option value="2">2 - {t('feedback.ratings.r2')}</option>
                <option value="3">3 - {t('feedback.ratings.r3')}</option>
              </select>
            </div>

            {/* Фильтр по ключевому слову */}
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('feedback.comments.keyword')}
              </label>
              <input
                type="text"
                value={filters.keyword}
                onChange={(e) => handleFilterChange('keyword', e.target.value)}
                className={COMPONENT_STYLES.form.input}
                placeholder={t('feedback.comments.search_placeholder')}
                list="keyword-suggestions"
              />
              <datalist id="keyword-suggestions">
                {keywordSuggestions.map((word, index) => (
                  <option key={index} value={word} />
                ))}
              </datalist>
            </div>
          </Flex>

          {/* Второй ряд фильтров */}
          <Flex direction="column" gap="sm">
            {/* Фильтр по дате (от) */}
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('feedback.comments.date_from')}
              </label>
              <input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value || null)}
                className={COMPONENT_STYLES.form.input}
              />
            </div>

            {/* Фильтр по дате (до) */}
            <div>
              <label className={COMPONENT_STYLES.form.label}>
                {t('feedback.comments.date_to')}
              </label>
              <input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e.target.value || null)}
                className={COMPONENT_STYLES.form.input}
              />
            </div>
          </Flex>
          
          {/* Кнопка сброса фильтров */}
          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.primary} w-full py-2 px-4`}
            >
              {t('feedback.comments.reset_filters')}
            </button>
          </div>
        </Grid>
      </div>

      {/* Результаты фильтрации */}
      <div className="mb-2 text-sm text-gray-500">
        {t('feedback.comments.showing_results', { 
          count: filteredComments.length, 
          total: comments.length 
        })}
      </div>

      {/* Если нет данных после фильтрации */}
      {filteredComments.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500">{t('feedback.comments.no_matching_comments')}</p>
        </div>
      ) : (
        <>
          {/* Используем UITable для мобильных устройств */}
          {isMobile ? (
            <UITable
              data={paginatedComments}
              columns={tableColumns}
              keyExtractor={(comment) => comment.id}
              isLoading={false}
              emptyText={t('feedback.comments.no_matching_comments')}
              scrollable={false}
              autoHeight={true}
              showPagination={false}
              responsive={true}
              mobileBehavior="cards"
              className="w-full"
              renderMobileCard={(comment) => (
                <Card padding="md" background="white" className="mb-4 shadow-sm">
                  <Flex direction="column" gap="sm">
                    <Flex justify="between" align="center">
                      <div className="text-sm text-gray-500">
                        {comment.formattedDate || formatSmartDate(comment.date, true, i18n.language)}
                      </div>
                      <span 
                        title={t(`feedback.ratings.r${comment.rating}`)} 
                        className="text-xl"
                      >
                        {getRatingEmoji(comment.rating)}
                      </span>
                    </Flex>
                    <div className={TEXT_STYLES.body}>
                      {comment.text}
                    </div>
                    <div className="text-xs text-gray-500">
                      {comment.platform}
                    </div>
                  </Flex>
                </Card>
              )}
            />
          ) : (
            /* Используем DataList для десктопной версии */
            <DataList 
              data={paginatedComments} 
              columns={columns} 
              noDataText={t('no_data')}
              className="bg-white rounded shadow-sm"
              rowClassName="hover:bg-gray-50"
            />
          )}

          {/* Пагинация */}
          {totalPages > 1 && (
            <Flex justify="between" align="center" className={styles.commentsPagination}>
              <div>
                <span className="text-sm text-gray-700">
                  {t('feedback.comments.page_info', { 
                    current: page, 
                    total: totalPages 
                  })}
                </span>
              </div>
              
              <Flex gap="md">
                {/* Кнопка Назад */}
                <button
                  type="button"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className={`${styles.paginationButton} ${styles.paginationButtonPrev}`}
                >
                  {t('feedback.comments.prev')}
                </button>
                
                {/* Кнопка Вперед */}
                <button
                  type="button"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className={`${styles.paginationButton} ${styles.paginationButtonNext}`}
                >
                  {t('feedback.comments.next')}
                </button>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </div>
  );
};